/**
 * HandoffsStatsModal Component
 * Modal to display handoff statistics when flywheel is enabled
 */

import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Typography, Select, useToast } from '@crisellevaldez/speculo-ui';
import { Inbox, Loader2, AlertCircle, Calendar } from 'lucide-react';
import { formatNumber } from '../../../utils/format';
import { useSettledCount } from '../../../hooks/useSettledCount';
import { useQueryClient } from '@tanstack/react-query';
import { useHandoffs } from '../../../hooks/dashboard/handoff';
import { HandoffStatus } from '../../../types/handoff.types';
import { useHandoffQueue } from '../../../hooks/handoff';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { EditContactModal } from '../contacts/EditContactModal';
import { HandoffCard } from './handoff';
import { cn } from '../../../utils/cn';
import { HandoffActionModal } from '../handoff/HandoffActionModal';
import { HandoffHistoryItem } from '../../../api/handoff';

// Status filter options (SPEC-2300 state machine)
const statusFilterOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Available to Claim' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'released', label: 'Returned to Remi' },
  { value: 'declined', label: 'Declined' },
  // The label stays "Expired"; the value has to be the status rows carry.
  { value: 'expired', label: 'Expired' },
];

interface HandoffsStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  orgTimezone?: string;
  handoffCount: number;
  status?: HandoffStatus | 'all';
}

export const HandoffsStatsModal: FC<HandoffsStatsModalProps> = ({
  isOpen,
  onClose,
  dateRange,
  orgTimezone,
  status = 'pending',
}) => {
  // Get organization ID for fetching details
  const organization = useOrganizationStore((state) => state.organization);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Local filter state (initialized from prop)
  const [statusFilter, setStatusFilter] = useState<string>(status);
  const [processingId, setProcessingId] = useState<string | null>(null);
  // Only decline confirms. Claiming is reversible — the agent can give the lead
  // back — so it goes through on the first tap.
  const [declineTarget, setDeclineTarget] = useState<HandoffHistoryItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{
    id: string;
    name: string;
    phone?: string;
    email?: string;
  } | null>(null);

  // Handle contact click to open edit modal
  const handleContactClick = (contact: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
  }) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  // Reset filter when modal opens with different status prop
  useEffect(() => {
    if (isOpen) {
      setStatusFilter(status);
    }
  }, [isOpen, status]);

  // Fetch handoffs from API with pagination
  const { handoffs, count, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useHandoffs({
      dateRange,
      status: statusFilter as HandoffStatus | 'all',
      enabled: isOpen, // Only fetch when modal is open
    });

  // Rows matching the active status filter, held steady while the filter's
  // refetch is in flight so the count never blinks to 0 mid-switch.
  const matchCount = useSettledCount(count ?? handoffs.length, isLoading);

  // Get claim/decline actions from handoff queue
  const { claim, decline, isClaiming, isDeclining } = useHandoffQueue({
    enabled: isOpen,
  });

  // Invalidate all handoff-related queries to force fresh data
  const invalidateHandoffQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['handoffs-history'] });
    queryClient.invalidateQueries({ queryKey: ['handoffs-stats'] });
    queryClient.invalidateQueries({ queryKey: ['handoff'] }); // For useHandoffQueue
  }, [queryClient]);

  // Handle claim action — on success we also drop the agent straight
  // into the contact edit modal so they can act on the lead they just
  // took. Without that they'd see the row flip to "claimed" and have to
  // click the contact name as a separate step, which is exactly what
  // the "redirect to actual contact on claim" ask was about.
  const handleClaim = async (handoffId: string, notes?: string) => {
    setProcessingId(handoffId);
    try {
      const result = await claim(handoffId, notes);
      if (result.success) {
        setDeclineTarget(null);
        showToast({ message: 'Handoff claimed successfully!' });
        invalidateHandoffQueries();
        const claimed = handoffs.find((h) => h.handoffId === handoffId);
        if (claimed?.contactId) {
          handleContactClick({
            id: claimed.contactId,
            name: claimed.contactName || '',
            phone: claimed.contactPhone || undefined,
            email: claimed.contactEmail || undefined,
          });
        }
      } else {
        showToast({ message: result.error || 'Failed to claim. Try again.' });
      }
    } catch (e) {
      console.error('Error claiming:', e);
      showToast({ message: 'Failed to claim. Something went wrong.' });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle decline action
  const handleDecline = async (handoffId: string) => {
    setProcessingId(handoffId);
    try {
      const result = await decline(handoffId, 'Declined in app');
      if (result.success) {
        setDeclineTarget(null);
        showToast({ message: 'Handoff declined. Remi will continue nurturing.' });
        invalidateHandoffQueries();
      } else {
        showToast({ message: result.error || 'Failed to decline. Try again.' });
      }
    } catch (e) {
      console.error('Error declining:', e);
      showToast({ message: 'Failed to decline. Something went wrong.' });
    } finally {
      setProcessingId(null);
    }
  };

  // Scroll container ref for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls within 200px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // The gutter that keeps cards off the scrollbar would otherwise sit there as
  // an unexplained margin whenever the list is short enough not to scroll.
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const measure = () => setIsScrollable(container.scrollHeight > container.clientHeight);
    measure();

    // Cards grow as details expand and as pages load, neither of which resizes
    // the container itself.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    const [content] = Array.from(container.children);
    if (content) observer.observe(content);

    return () => observer.disconnect();
  }, [handoffs.length, isLoading, statusFilter]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <Typography variant="h2">Handoffs</Typography>
              {/* Handoffs are an all-time live queue, not a date-windowed
                  report, so the chip is fixed to "All Time" rather than the
                  dashboard's parent date range. */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
                <Calendar className="h-3.5 w-3.5" />
                <span>All Time</span>
              </div>
            </div>
            {matchCount !== undefined && (
              <div className="text-xs text-muted-foreground">
                {formatNumber(matchCount)} {matchCount === 1 ? 'handoff' : 'handoffs'}
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="flex-1 flex flex-col min-h-0 !bg-muted">
          {/* Filter bar — left-aligned */}
          <div className="flex flex-wrap items-end gap-3 w-full mb-4 shrink-0">
            <Select
              options={statusFilterOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(Array.isArray(value) ? value[0] : value)}
              className="w-full sm:w-[200px]"
            />
          </div>

          {/* The gutter keeps cards clear of the scrollbar — and their shadows —
              but only once there is a scrollbar to clear. */}
          <div
            ref={scrollContainerRef}
            className={cn(
              'flex-1 min-h-0 overflow-y-auto scrollbar-custom',
              isScrollable && 'pr-2 sm:pr-3'
            )}
          >
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Loading handoffs…</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-destructive">
                <AlertCircle className="h-8 w-8" />
                <p className="text-sm">Couldn't load handoffs — {error.message}</p>
              </div>
            ) : handoffs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                <div className="h-14 w-14 rounded-full bg-foreground/5 flex items-center justify-center">
                  <Inbox className="h-7 w-7 opacity-60" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground/80">
                    No{' '}
                    {statusFilter !== 'all'
                      ? statusFilterOptions
                          .find((o) => o.value === statusFilter)
                          ?.label.toLowerCase()
                      : ''}{' '}
                    handoffs
                  </p>
                  <p className="text-xs mt-1">
                    Nothing matches the current filter for this period.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {handoffs.map((handoff) => (
                  <HandoffCard
                    key={handoff.handoffId}
                    handoff={handoff}
                    orgTimezone={orgTimezone}
                    organizationId={organization?.id}
                    onClaim={() => handleClaim(handoff.handoffId)}
                    onDecline={async () => setDeclineTarget(handoff)}
                    onContactClick={handleContactClick}
                    isClaiming={isClaiming}
                    isDeclining={isDeclining}
                    processingId={processingId}
                  />
                ))}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading more…</span>
                  </div>
                )}
                {!hasNextPage && handoffs.length > 0 && (
                  <p className="text-center text-[11px] uppercase tracking-wider text-muted-foreground/60 py-2">
                    End of list
                  </p>
                )}
              </div>
            )}
          </div>
        </Modal.Body>

        {/* The list loads a page at a time as you scroll, so the footer says
            how far through the current status filter's matches you are. */}
        {matchCount !== undefined && (
          <Modal.Footer>
            <span className="text-xs sm:text-sm text-muted-foreground">
              Showing{' '}
              <span className="font-semibold text-foreground">{formatNumber(handoffs.length)}</span>{' '}
              of <span className="font-semibold text-foreground">{formatNumber(matchCount)}</span>{' '}
              {matchCount === 1 ? 'handoff' : 'handoffs'}
            </span>
          </Modal.Footer>
        )}
      </Modal>
      {/* Fresh instance per target — see HandoffNotification for why. */}
      <HandoffActionModal
        key={declineTarget?.handoffId ?? 'closed'}
        isOpen={declineTarget !== null}
        contactName={declineTarget?.contactName || 'this lead'}
        contactPhone={declineTarget?.contactPhone}
        isSubmitting={isDeclining && processingId === declineTarget?.handoffId}
        onClose={() => setDeclineTarget(null)}
        onConfirm={() => {
          if (declineTarget) handleDecline(declineTarget.handoffId);
        }}
      />
      {showEditModal && selectedContact && (
        <EditContactModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedContact(null);
          }}
          contact={{ id: selectedContact.id }}
          onSubmit={async () => {
            // Contact updates are handled internally by EditContactModal
          }}
          onSuccess={() => {
            invalidateHandoffQueries();
          }}
        />
      )}
    </>
  );
};

export default HandoffsStatsModal;
